// Global precharting paste tab: paste EHR text -> review parsed result -> apply to the chart sections.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ClipboardPaste,
  Copy,
  FileText,
  Info,
  Loader2,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Textarea } from '../../ui/textarea';
import { enrichWithDatabase } from '../../../utils/prechartingEnrichment';
import {
  PASTE_TEMPLATE,
  SECTION_LABELS,
  buildChartUpdates,
  parseHeadinglessText,
  parsePrechartingText,
} from '../../../utils/prechartingPasteParser';

const DISPLAY_ORDER = [
  'problemList',
  'medicationList',
  'vitalsLabs',
  'surgicalHistory',
  'hospitalization',
  'diagnosticImaging',
  'procedures',
  'specialistsVisits',
];

/* ----------------------------- helpers ----------------------------------- */

const hasAnyValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasAnyValue);
  if (typeof value === 'object') return Object.values(value).some(hasAnyValue);
  return false;
};

const countExisting = (key, data) => {
  if (!data) return 0;
  switch (key) {
    case 'problemList':
      return Array.isArray(data.problemList) ? data.problemList.length : 0;
    case 'medicationList':
      return Array.isArray(data.medicationList?.medications) ? data.medicationList.medications.length : 0;
    case 'vitalsLabs':
      return hasAnyValue(data.vitalsLabs) ? 1 : 0;
    case 'surgicalHistory':
    case 'hospitalization':
    case 'diagnosticImaging': {
      const arr = data.diagnosticImaging?.[key];
      return Array.isArray(arr) ? arr.length : 0;
    }
    case 'procedures':
      return Array.isArray(data.procedures) ? data.procedures.length : 0;
    case 'specialistsVisits':
      return Array.isArray(data.specialistsVisits) ? data.specialistsVisits.length : 0;
    default:
      return 0;
  }
};

const join = (parts, sep = ' · ') => parts.filter((p) => p && String(p).trim()).join(sep);

const describeItem = (key, item) => {
  switch (key) {
    case 'problemList':
      return { title: item.code, detail: item.description || '' };
    case 'medicationList':
      return {
        title: item.name,
        detail: join([item.status !== 'taking' ? item.status : '', item.dosage, item.formulation, item.frequency, item.timing, item.indication]),
      };
    case 'surgicalHistory':
      return { title: item.surgery, detail: item.date };
    case 'hospitalization':
      return { title: item.reason, detail: item.date };
    case 'diagnosticImaging':
      return { title: join([item.date, item.locationContrast], ' — '), detail: join([item.indication, item.result]) };
    case 'procedures':
      return { title: item.name, detail: join([item.date, item.indication, item.result]) };
    case 'specialistsVisits':
      return { title: join([item.specialty, item.specialistName], ' — '), detail: join([item.date, item.details]) };
    default:
      return { title: '', detail: '' };
  }
};

const describeVitals = (m) => {
  let value = '';
  if (m.value !== undefined && m.value !== '') value = m.value;
  else if (m.before !== undefined || m.after !== undefined) {
    value = join([m.before ? `Before: ${m.before}` : '', m.after ? `After: ${m.after}` : ''], '  →  ');
  }
  return value;
};

/* ----------------------------- component --------------------------------- */

/**
 * Props:
 *  - prechartingData: current chart data (used to warn about sections that will be replaced)
 *  - onApply(updates, mode): mode is 'replace' (selected sections are replaced) or 'append' (new entries are
 *      added to what is already there)
 *  - defaultSectionKey: the section of the tab this paste box is shown in. Pasted text WITHOUT any recognised
 *      heading is read as this section (or stored where it clearly belongs). Text WITH headings is always
 *      split into the right sections, whichever tab it is pasted in.
 *  - defaultMode: initial 'replace' | 'append' choice
 *  - embedded: compact layout (used inside the per-tab dialog)
 *  - onApplied(): called after the result was applied (e.g. to close the dialog)
 */
const GlobalPasteTab = ({
  prechartingData,
  onApply,
  readonly = false,
  defaultSectionKey = null,
  defaultMode = 'replace',
  embedded = false,
  onApplied,
}) => {
  const [text, setText] = useState('');
  const [mode, setMode] = useState(defaultMode);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [isParsing, setIsParsing] = useState(false);
  const runIdRef = useRef(0);

  const resetPreview = useCallback(() => {
    runIdRef.current += 1;
    setPreview(null);
    setSelected({});
    setIsParsing(false);
  }, []);

  const handleTextChange = (e) => {
    setText(e.target.value);
    if (preview) resetPreview(); // the preview no longer matches the text
  };

  const handleLoadTemplate = () => {
    setText(PASTE_TEMPLATE);
    if (preview) resetPreview();
  };

  const handleCopyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(PASTE_TEMPLATE);
      toast.success('Template copied');
    } catch (e) {
      toast.error('Could not copy. Use "Load template" instead.');
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (!clip) {
        toast.error('Clipboard is empty');
        return;
      }
      setText(clip);
      if (preview) resetPreview();
    } catch (e) {
      toast.error('Clipboard access was blocked. Paste into the box with Ctrl+V.');
    }
  };

  const handleParse = async () => {
    if (!text.trim()) {
      toast.error('Paste some precharting text first');
      return;
    }
    runIdRef.current += 1;
    const runId = runIdRef.current;
    setIsParsing(true);
    try {
      let parsed = parsePrechartingText(text);
      // No recognised heading: read the text as the tab it was pasted in (or where it clearly belongs)
      if (Object.keys(parsed.sections).length === 0 && defaultSectionKey) {
        parsed = parseHeadinglessText(defaultSectionKey, text);
      }
      const enriched = await enrichWithDatabase(parsed);
      if (runId !== runIdRef.current) return;
      const initial = {};
      DISPLAY_ORDER.forEach((key) => {
        const s = enriched.sections[key];
        initial[key] = !!s && s.items.length > 0;
      });
      setPreview(enriched);
      setSelected(initial);
    } catch (e) {
      console.error('Precharting paste parse failed:', e);
      if (runId === runIdRef.current) toast.error('Could not read the pasted text');
    } finally {
      if (runId === runIdRef.current) setIsParsing(false);
    }
  };

  const removeItem = (key, index) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const section = prev.sections[key];
      const items = section.items.filter((_, i) => i !== index);
      return { ...prev, sections: { ...prev.sections, [key]: { ...section, items } } };
    });
  };

  const selectedKeys = useMemo(
    () => (preview ? DISPLAY_ORDER.filter((k) => selected[k] && preview.sections[k]?.items.length > 0) : []),
    [preview, selected]
  );

  const willOverwrite = useMemo(
    () => selectedKeys.filter((k) => countExisting(k, prechartingData) > 0),
    [selectedKeys, prechartingData]
  );

  const handleApply = () => {
    if (!preview || selectedKeys.length === 0 || readonly) return;
    if (mode === 'replace' && willOverwrite.length > 0) {
      const names = willOverwrite.map((k) => `• ${SECTION_LABELS[k]}`).join('\n');
      const ok = window.confirm(`These sections already have data and it will be REPLACED:\n\n${names}\n\nContinue?`);
      if (!ok) return;
    }
    const updates = buildChartUpdates(preview.sections, selectedKeys);
    onApply(updates, mode);
    toast.success(
      `${mode === 'append' ? 'Added to' : 'Applied'} ${selectedKeys.length} section${selectedKeys.length === 1 ? '' : 's'}`
    );
    resetPreview();
    if (embedded) setText('');
    if (onApplied) onApplied();
  };

  const presentKeys = preview ? DISPLAY_ORDER.filter((k) => preview.sections[k]) : [];
  const unrecognized = preview?.unrecognized || [];
  const skipped = preview?.skipped || [];

  return (
    <div className="space-y-6">
      <Card className={embedded ? 'border-0 shadow-none' : ''}>
        {!embedded && (
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ClipboardPaste className="h-5 w-5 text-blue-500" />
              Global Precharting Paste
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className={embedded ? 'space-y-4 p-0' : 'space-y-4'}>
          {embedded ? (
            <p className="text-sm text-gray-600">
              Paste text with its headings (Diagnosis List, Current Medications, Surgical History, Hospitalization,
              Vital Signs, Labs/DI/Reports…). Each part is stored in its own section, whichever tab you paste it
              from. Text without a heading is read as{' '}
              <strong>{SECTION_LABELS[defaultSectionKey] || 'this tab'}</strong>. You review everything before it is
              added.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              Paste the patient's precharting data from the EHR (eClinicalWorks layout). Each part is matched by its
              heading: Diagnosis List (numbered ICD-10 lines, several codes on a line are split), Current Medications
              (Taking / Not-Taking / Discontinued), Surgical History,
              Hospitalization/Major Diagnostic Procedure, Vital Signs and Labs/DI/Reports. Other headings such as
              Social History or Allergies are ignored. You will review everything before it is applied.
            </p>
          )}
          <p className="text-xs text-gray-500">
            Diagnostic Imaging, Procedures and Specialists have no eCW heading. Write one entry per line with
            commas (or &quot; - &quot;) between the fields:{' '}
            <span className="font-mono">date, study, indication, result</span>,{' '}
            <span className="font-mono">date, procedure, indication, result</span>,{' '}
            <span className="font-mono">date, specialty, Dr. Name, details</span>. Click &quot;Load template&quot; to see
            every section.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleLoadTemplate} disabled={readonly}>
              <FileText className="h-4 w-4 mr-2" />
              Load template
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleCopyTemplate}>
              <Copy className="h-4 w-4 mr-2" />
              Copy template
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handlePasteFromClipboard} disabled={readonly}>
              <ClipboardPaste className="h-4 w-4 mr-2" />
              Paste from clipboard
            </Button>
            {text && !readonly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setText('');
                  resetPreview();
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
          <Textarea
            value={text}
            onChange={handleTextChange}
            readOnly={readonly}
            placeholder={'Diagnosis List\n1) E11.9 -- Type 2 diabetes mellitus without complications\n2) I10 -- Essential (primary) hypertension\n\nCurrent Medications\nTaking\nMetformin 500 mg tablet 1 tab(s) orally 2 times a day\nNot-Taking\nNaproxen 500 mg tablet 1 tab(s) orally 2 times a day as needed\n\nSurgical History\nAppendectomy 2001\n\nHospitalization/Major Diagnostic Procedure\nPneumonia 11/2022\n\nVital Signs\nBP 128/82 mm of Hg, Wt 180 lbs, BMI 28.50 Index\n\nLabs/DI/Reports\nHbA1c 7.5 % 06/10/2024\n...'}
            className={embedded ? 'min-h-[200px] font-mono text-sm' : 'min-h-[260px] font-mono text-sm'}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{text.length.toLocaleString()} characters</span>
            <Button type="button" onClick={handleParse} disabled={isParsing || readonly || !text.trim()}>
              {isParsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
              {isParsing ? 'Reading and verifying…' : 'Review parsed data'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <>
          {preview.headingsFound === 0 && preview.targetKey && presentKeys.length > 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                No section heading was found, so the text was read as{' '}
                <strong>{SECTION_LABELS[preview.targetKey]}</strong>
                {preview.redirectedTo
                  ? ` because it looks like ${SECTION_LABELS[preview.targetKey]} (not ${SECTION_LABELS[defaultSectionKey]}).`
                  : ' (the tab you pasted it in).'}
                {' '}Add a heading such as "Current Medications" to send each part to its own section.
              </AlertDescription>
            </Alert>
          )}

          {presentKeys.length === 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No section headings were recognised, so nothing can be applied. Start each part with a heading such
                as "Problem List" or "Medications" (use "Load template" to see the layout).
              </AlertDescription>
            </Alert>
          )}

          {presentKeys.map((key) => {
            const section = preview.sections[key];
            const count = section.items.length;
            const existing = countExisting(key, prechartingData);
            const checked = !!selected[key] && count > 0;
            return (
              <Card key={key} className={checked ? 'border-blue-300' : ''}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex flex-wrap items-center gap-3 text-base">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={checked}
                      disabled={count === 0}
                      onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
                      aria-label={`Apply ${SECTION_LABELS[key]}`}
                    />
                    <span>{SECTION_LABELS[key]}</span>
                    <Badge variant={count > 0 ? 'default' : 'secondary'}>
                      {key === 'vitalsLabs' ? `${count} field${count === 1 ? '' : 's'}` : `${count} item${count === 1 ? '' : 's'}`}
                    </Badge>
                    {checked && existing > 0 && mode === 'replace' && (
                      <span className="flex items-center gap-1 text-xs font-normal text-amber-700">
                        <AlertTriangle className="h-3 w-3" />
                        {key === 'vitalsLabs'
                          ? 'replaces the existing vitals & labs'
                          : `replaces ${existing} existing ${existing === 1 ? 'entry' : 'entries'}`}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {count === 0 ? (
                    <p className="text-sm text-gray-500">Nothing usable was found under this heading.</p>
                  ) : key === 'vitalsLabs' ? (
                    <div className="divide-y rounded-md border">
                      {section.items.map((m, i) => (
                        <div key={`${key}-${i}`} className="flex flex-col gap-1 px-3 py-2 text-sm sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <span className="font-medium">{m.label}</span>
                            <span className="ml-2 text-gray-800">{describeVitals(m)}</span>
                            {(m.notes || []).map((n, j) => (
                              <div key={j} className="text-xs text-amber-700">{n}</div>
                            ))}
                          </div>
                          <span className="text-xs text-gray-400 sm:max-w-[40%] sm:text-right">{m.source}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="divide-y rounded-md border">
                      {section.items.map((item, i) => {
                        const d = describeItem(key, item);
                        return (
                          <li key={`${key}-${i}`} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                            <div className="min-w-0">
                              <span className="font-medium break-words">{d.title || '(no text)'}</span>
                              {d.detail && <span className="ml-2 text-gray-700 break-words">{d.detail}</span>}
                              {key === 'problemList' && item.dbVerified === false && (
                                <div className="text-xs text-amber-700">
                                  Not verified in the ICD-10-CM database; kept as pasted.
                                </div>
                              )}
                              {key === 'medicationList' && item.dbVerified === false && (
                                <div className="text-xs text-amber-700">
                                  Not found in the medication database; will be added as an unvalidated entry.
                                </div>
                              )}
                              {key === 'medicationList' && item.dbVerified && (
                                <div className="text-xs text-green-700 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" /> Verified
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 shrink-0 p-0 text-gray-500 hover:text-red-600"
                              onClick={() => removeItem(key, i)}
                              aria-label="Remove this item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {section.warnings.length > 0 && (
                    <ul className="space-y-1 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
                      {section.warnings.map((w, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                          <span>
                            {w.lineNumber ? `Line ${w.lineNumber}: ` : ''}
                            {w.message}
                            {w.text ? <span className="text-amber-600"> ("{w.text}")</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {unrecognized.length > 0 && (
            <Card className="border-amber-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  Not added ({unrecognized.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-2 text-xs text-gray-600">
                  These lines could not be understood with certainty, so they were left out. Add them manually in the
                  relevant tab if needed.
                </p>
                <ul className="divide-y rounded-md border text-sm">
                  {unrecognized.map((u, i) => (
                    <li key={i} className="px-3 py-2">
                      <div className="break-words font-mono text-xs">{u.text}</div>
                      <div className="text-xs text-gray-500">
                        {u.section ? `${SECTION_LABELS[u.section]} · ` : ''}
                        line {u.lineNumber} · {u.reason}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {(skipped.length > 0 || preview.ignoredSections.length > 0) && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="space-y-2 text-xs">
                {preview.ignoredSections.length > 0 && (
                  <div>
                    <span className="font-medium">Sections not part of precharting (ignored): </span>
                    {preview.ignoredSections.map((s) => `${s.heading}${s.lineCount ? ` (${s.lineCount} lines)` : ''}`).join(', ')}
                  </div>
                )}
                {skipped.length > 0 && (
                  <div>
                    <div className="font-medium">Recognised but not tracked in precharting ({skipped.length}):</div>
                    <ul className="list-disc pl-5">
                      {skipped.map((s, i) => (
                        <li key={i} className="break-words">{s.text} <span className="text-gray-500">({s.reason})</span></li>
                      ))}
                    </ul>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-4 rounded-md border bg-gray-50 px-3 py-2 text-sm">
            <span className="font-medium text-gray-700">If a section already has data:</span>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`paste-mode-${embedded ? 'modal' : 'tab'}`}
                checked={mode === 'append'}
                onChange={() => setMode('append')}
              />
              Add to existing
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name={`paste-mode-${embedded ? 'modal' : 'tab'}`}
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
              />
              Replace existing
            </label>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
            <span className="text-sm text-gray-600">
              {selectedKeys.length === 0
                ? 'Select at least one section to apply.'
                : `${selectedKeys.length} section${selectedKeys.length === 1 ? '' : 's'} selected`}
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={resetPreview}>
                Discard
              </Button>
              <Button
                type="button"
                onClick={handleApply}
                disabled={readonly || selectedKeys.length === 0}
                className="bg-green-600 text-white hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {mode === 'append' ? 'Add to precharting' : 'Apply to precharting'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalPasteTab;
